/* eslint-disable react/button-has-type */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { useHistory } from 'react-router-dom';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import VoiceIcon from '@src/assets/icons/voice.svg';
import AIIcon from '@src/assets/icons/ai.svg';
import WaveSoundIcon from '@src/assets/icons/wave-sound.png';
import CrownImg from '@src/assets/images/crown.png';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  PACKAGE_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import ROUTES from '@src/constants/route';
import { MOBILE_BREAKPOINT } from '@src/constants';
import useCustomKeycloak from '@src/hooks/useCustomKeycloak';
import { CRM_URL, LANDING_PAGE_URL } from '@src/configs';
import {
  AIVOICE_TTS_APP_STORE_URL,
  AIVOICE_TTS_DEEPLINK,
  AIVOICE_TTS_GOOGLE_PLAY_URL,
  DOWNLOAD_APP_LINK,
  DOWNLOAD_APP_LINK_TEXT,
} from '@src/constants/mobileApp';
import {
  CLONE_VOICE,
  ORIGINAL_VOICE,
  TIME_EXPIRE_QR_VOICE_CLONING,
} from '@src/constants/voiceCloning';
import { MOBILE_OS } from '@src/constants/os';
import apis from '@src/apis';
import { getMobileOperatingSystem } from '@src/utils/osMobile';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import useUserInfo from '@src/hooks/useTtsUser';
import actions from '@src/redux/actions';
import { StyledFirstVoiceV2 } from './index.style';
import QRDialog from './QRDialog';
import { VOICE_CLONING_SAMPLE_AUDIO } from './sampleAudio';

const ThreeSteps = ({ isMobile }) => {
  const { t } = useTranslation();
  const desktopSpanStyle = {
    borderRadius: '8px',
    padding: '1px 2px',
    background: '#FFD60A',
    color: '#1C1C1E',
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: '28px',
    wordWrap: 'break-word',
  };

  const mobileSpanStyle = {
    borderRadius: '8px',
    padding: '1px 2px',
    background: '#FFD60A',
    color: '#1C1C1E',
    fontSize: '16px',
    fontWeight: '700',
    wordWrap: 'break-word',
    lineHeight: '24px',
  };

  return isMobile ? (
    <>
      <br />
      <span style={mobileSpanStyle}>{t('threeEasySteps')}</span>
    </>
  ) : (
    <span style={desktopSpanStyle}>{t('threeEasySteps')}</span>
  );
};

const VoiceItem = ({ voiceItem, isVoiceClone, isPlaying, onTogglePlay }) => {
  const { t } = useTranslation();
  return (
    <Box className="voice-cloning-demo-wrapper">
      <img src={voiceItem.icon} alt="icon-voice-1" />
      <Box className="demo-item">
        <Box className="demo-title">
          <Typography className="voice-name">{voiceItem.name}</Typography>
          <Box
            className={
              isVoiceClone ? 'clone-voice-label' : 'original-voice-label'
            }
          >
            {isVoiceClone ? t('voiceClone') : t('originalVoice')}
          </Box>
        </Box>
        <Typography className="voice-desc">{voiceItem.description}</Typography>
      </Box>
      <IconButton
        onClick={onTogglePlay}
        className={isPlaying ? 'pause-icon' : 'play-icon'}
      >
        {isPlaying ? (
          <PauseRoundedIcon className="sound-icon" />
        ) : (
          <PlayArrowRoundedIcon className="sound-icon" />
        )}
      </IconButton>
    </Box>
  );
};

const FirstVoiceV2 = () => {
  const { keycloak } = useCustomKeycloak();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const { openSidebar } = useSelector((state) => state.layout);
  const { getFeatureValue } = useFeatureFlags();

  const isExpiredPackage =
    !ttsUser?.packageExpiryDate || moment().isAfter(ttsUser?.packageExpiryDate);

  const checkUserCanUseVoiceCloning =
    getFeatureValue(FEATURE_KEYS.VOICE_CLONING, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    }) ||
    (validateUserPackageCanUseVoiceCloning(ttsUser) && !isExpiredPackage);

  const isNewQRFlow = getFeatureValue(FEATURE_KEYS.NEW_QR_FLOW, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrExpireDate, setQrExpireDate] = useState('');
  const [qrRedirectUrl, setQRRedirectUrl] = useState('');

  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [currentAudioType, setCurrentAudioType] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [os, setOs] = useState(null);

  const [redirectPaymentUrl, setRedirectPaymentUrl] = useState('');

  const changeDirectionListVoicesWebSidebarOpen =
    windowWidth <= 1170 && openSidebar;
  const changeDirectionListVoicesWebSidebarClose =
    windowWidth <= 990 && !openSidebar;
  const changeDirectionListVoicesMobile = windowWidth <= 989;
  const isMobile = windowWidth < 600;

  const changeDirectionList =
    changeDirectionListVoicesWebSidebarOpen ||
    changeDirectionListVoicesWebSidebarClose ||
    changeDirectionListVoicesMobile;

  const voiceCloningSteps = [
    {
      title: `${t('step')} 1: ${t('prepareVoice')}`,
      description: t('prepareVoiceDesc'),
      icon: VoiceIcon,
    },
    {
      title: `${t('step')} 2: ${t('traningVoice')}`,
      description: t('traningVoiceDesc'),
      icon: AIIcon,
    },
    {
      title: `${t('step')} 3: ${t('voiceCloningResult')}`,
      description: t('voiceCloningResultDesc'),
      icon: WaveSoundIcon,
    },
  ];

  const generateQRCodeUrl = async (loginCode) => {
    if (isNewQRFlow) {
      const screenApp = 'voiceCloning';
      return `${LANDING_PAGE_URL}/apps/aivoice-tts?screen=${screenApp}&loginCode=${loginCode}`;
    }

    let response;
    try {
      response = await apis.shortlink.generateShortlink({
        webAppUrl: `${CRM_URL}/studio/home?loginCode=${loginCode}`,
        deeplink: `${AIVOICE_TTS_DEEPLINK.LOGIN}?loginCode=${loginCode}`,
        appStoreUrl: AIVOICE_TTS_APP_STORE_URL,
        googlePlayUrl: AIVOICE_TTS_GOOGLE_PLAY_URL,
        ttl: TIME_EXPIRE_QR_VOICE_CLONING,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('errorGenerateQRCode', error);
    }
    if (!response?.status || !response?.result?.shortlink) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorGenerateQRCode'),
        }),
      );
      return null;
    }
    return response?.result?.shortlink;
  };

  const handleOpenQR = async (redirectUrl) => {
    setQRRedirectUrl(redirectUrl);

    const loginCode = await keycloak.generateLoginCode(redirectUrl);
    if (!loginCode) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorGenerateQRCode'),
        }),
      );
      return;
    }
    const url = await generateQRCodeUrl(loginCode);
    if (!url) return;

    setQrExpireDate(moment().add(TIME_EXPIRE_QR_VOICE_CLONING, 'milliseconds'));
    setQrCodeUrl(url);
  };

  const handleCloseQRDialog = () => setOpenQRDialog(false);

  const handleClickDownloadApp = () => {
    if (!os) return;

    let isFallbackTriggered = false;
    let fallbackUrl;

    if (os === MOBILE_OS.ANDROID || os === MOBILE_OS.WIN_PHONE)
      fallbackUrl = AIVOICE_TTS_GOOGLE_PLAY_URL;
    if (os === MOBILE_OS.IOS) fallbackUrl = AIVOICE_TTS_APP_STORE_URL;

    if (fallbackUrl) {
      const timer = setTimeout(() => {
        if (!isFallbackTriggered) window.location.href = fallbackUrl;
      }, 3000);

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          clearTimeout(timer);
          isFallbackTriggered = true;
          window.location.href = fallbackUrl;
        }
      };

      window.location.href = AIVOICE_TTS_DEEPLINK.VOICE_CLONING;
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // eslint-disable-next-line consistent-return
      return () => {
        clearTimeout(timer);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      };
    }
  };

  const handleFetchPackages = async () => {
    try {
      const res = await apis.packages.getPackages({
        active: true,
        purchasable: true,
        type: PACKAGE_TYPE.STUDIO,
      });
      if (res?.status) {
        const packageCodes = res?.result?.packages?.map((item) => item.code);
        const highestPackageCode = packageCodes.find(
          (item) =>
            (item.includes(PACKAGE_LEVEL.SPECIAL) ||
              item.includes(PACKAGE_LEVEL.ENTERPRISE)) &&
            item.includes(PACKAGE_DURATION.YEAR),
        );
        setRedirectPaymentUrl(
          `${ROUTES.PAYMENT}?package=${highestPackageCode}`,
        );
      }
    } catch (error) {
      setRedirectPaymentUrl(ROUTES.PAYMENT);
    }
  };

  const handleClickCreate = async () => {
    if (!checkUserCanUseVoiceCloning) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceCloningUpgrade'),
          img: CrownImg,
          classNameImg: 'custom-img',
          showButtonUnderstand: true,
          onUpgrade: () => {
            history.push(redirectPaymentUrl);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }

    if (isMobile) {
      handleClickDownloadApp();
      return;
    }

    const redirectUrl = `${CRM_URL}${ROUTES.VOICE_CLONING_CREATE}`;
    await handleOpenQR(redirectUrl);
    setOpenQRDialog(true);
  };

  const handlePlayPause = (audioSrc, index, audioType) => {
    if (currentIndex === index && currentAudioType === audioType) {
      if (currentAudio.paused) {
        currentAudio.play();
      } else {
        currentAudio.pause();
        setCurrentIndex(null);
        setCurrentAudioType(null);
      }
      return;
    }

    if (currentAudio) {
      currentAudio.pause();
    }

    const newAudio = new Audio(audioSrc);
    newAudio.play();
    setCurrentAudio(newAudio);
    setCurrentIndex(index);
    setCurrentAudioType(audioType);

    newAudio.onended = () => {
      setCurrentIndex(null);
      setCurrentAudio(null);
      setCurrentAudioType(null);
    };
  };

  const renderIconCreateVoiceOnMobileWeb = () => (
    <Box className="intro-mobile-web-container">
      <button
        onClick={handleClickCreate}
        className="button-create-voice button-create-voice-mobile"
      >
        <AddRoundedIcon /> {t('createVoice')}
      </button>
      <Box className="intro-mobile-web">
        <IconButton className="info-icon">
          <InfoRoundedIcon />
        </IconButton>
        <Typography
          className="intro-mobile-web-text"
          dangerouslySetInnerHTML={{
            __html: t('mobileAppSupportMessage', {
              link: DOWNLOAD_APP_LINK,
              urlText: DOWNLOAD_APP_LINK_TEXT,
            }),
          }}
        />
      </Box>
    </Box>
  );

  useEffect(() => {
    const osDetected = getMobileOperatingSystem();
    setOs(osDetected);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    handleFetchPackages();
  }, []);

  return (
    <>
      <StyledFirstVoiceV2
        isMobile={isMobile}
        changeDirectionList={changeDirectionList}
      >
        <Box className="voice-cloning-title-container">
          <Typography className="voice-cloning-title">
            {t('experienceVoiceCloning')}
          </Typography>
          <Typography className="voice-cloning-description">
            {t('descriptionVoiceCloning')} <ThreeSteps isMobile={isMobile} />{' '}
            {t('suffixVoiceCloningDesc')}
          </Typography>
        </Box>

        {windowWidth >= MOBILE_BREAKPOINT && (
          <Box className="voice-cloning-step-container">
            {voiceCloningSteps.map((step) => (
              <Box className="step">
                <img src={step.icon} alt="step-icon" className="step-icon" />
                <Box>
                  <Typography className="step-title">{step.title}</Typography>
                  <Typography className="step-description">
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {windowWidth < MOBILE_BREAKPOINT && renderIconCreateVoiceOnMobileWeb()}

        <Box className="voice-cloning-demo-container">
          {VOICE_CLONING_SAMPLE_AUDIO.map((voiceItem, index) => (
            <Box className="voice-cloning-demo">
              <VoiceItem
                voiceItem={voiceItem}
                isPlaying={
                  currentIndex === index && currentAudioType === ORIGINAL_VOICE
                }
                onTogglePlay={() =>
                  handlePlayPause(
                    voiceItem.originalVoice,
                    index,
                    ORIGINAL_VOICE,
                  )
                }
              />
              {changeDirectionList ? (
                <IconButton sx={{ padding: 0 }}>
                  <KeyboardArrowDownRoundedIcon />
                </IconButton>
              ) : (
                <IconButton sx={{ padding: 0 }}>
                  <KeyboardArrowRightRoundedIcon />
                </IconButton>
              )}
              <VoiceItem
                voiceItem={voiceItem}
                isVoiceClone
                isPlaying={
                  currentIndex === index && currentAudioType === CLONE_VOICE
                }
                onTogglePlay={() =>
                  handlePlayPause(voiceItem.cloneVoice, index, CLONE_VOICE)
                }
              />
            </Box>
          ))}
        </Box>

        {windowWidth >= MOBILE_BREAKPOINT ? (
          <button onClick={handleClickCreate} className="button-create-voice">
            <AddRoundedIcon /> {t('createVoice')}
          </button>
        ) : (
          <br />
        )}
        <div className="emtpy-div" />
      </StyledFirstVoiceV2>
      <QRDialog
        open={openQRDialog}
        urlToShow={qrCodeUrl}
        qrExpireDate={qrExpireDate}
        qrRedirectUrl={qrRedirectUrl}
        onClose={handleCloseQRDialog}
        refreshQRCode={handleOpenQR}
      />
    </>
  );
};

export default FirstVoiceV2;
